import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Slider from 'react-slick-adaptive-height-fixed';

import 'slick-carousel/slick/slick.css';

import { ItemProvider, useItemProviderContext } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { imageSize } from '@common/react/utils/utils';
import { Lang } from '@common/typescript/objects/Lang';
import { ItemEditorWrapper } from '@common/react/components/Core/ItemEditor/ItemEditorWrapper';
import { List } from '@common/typescript/objects/List';

import { Location, PageLocation } from '@commonTuna/react/objects/BaseLocation';
import { Review } from '@commonTuna/react/objects/Review';

import ServerPageProvider from '@app/components/UI/SeverPageProviderWithLang/ServerpageProviderWithLang';
import '@app/scss/pages/doctorPage.scss';
import '@app/scss/pages/clinic.scss';
import { SimpleBreadCrumbs } from '@app/components/UI/SimpleBreadcrumbs/SimpleBreadcrumbs';
import ShareLinks from '@app/components/UI/ShareLinks/ShareLinks';
import DoctorCard from '@app/components/Pages/Home/DoctorCard';
import { reviewsSliderSettings, sliderSettings } from '@app/components/Utils';
import ContactUsForm from '@app/components/Forms/ContactUs/ContactUsForm';
import '@app/scss/components/slick-theme.scss';
import Reviews from '@app/components/UI/Reviews/Reviews';
import { OfficeInfoBlock } from '@app/components/Pages/Home/OfficeInfoBlock';
import Rate from '@app/components/UI/Rate/Rate';
import { ApplicationState } from '@app/store';
import PageLocations from '@app/components/Pages/Clinic/PageLocations';

interface DoctorInnerProps {
	load: () => Promise<any>;
}

interface LocationWithReviews extends Location {
	reviews: Array<Review>;
}

const onClick = (id) => {
	document.getElementById(id)
		?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

const ClinicInner: React.FC<DoctorInnerProps> = ({ load }) => {
	const context = useItemProviderContext<LocationWithReviews>();
	const location = useLocation();

	const [skipLoad, setSkipLoad] = React.useState(true);
	const lang = useSelector((state: ApplicationState) => state.login.lang);

	if (!context.state) throw 'Need ItemProvider context!';

	const { state: { item } } = context;

	const { i18n, t } = useTranslation();
	const isEnglish = i18n.language === 'en';

	const name = isEnglish ? item.nameEn : item.nameEs;

	const avatars = [item.siteAvatar, ...item.files.map((q) => q.file.src)];

	React.useEffect(() => {
		const newPath = lang === Lang.Es ? `/es/clinic/${item?.pathEs}` : `/clinic/${item?.pathEn}`;
		if (!skipLoad && newPath !== location.pathname) {
			load();
		}
		setSkipLoad(false);
	}, [location.pathname]);

	return (
		<div className="doctor-page inner-page container">
			<div className="clearfix mb10">
				<SimpleBreadCrumbs title={name} links={[]} />
				<ShareLinks />
			</div>
			<div className="clearfix">
				{avatars.length > 0 && <div className="doctor-page__image">
					<Slider {...sliderSettings} dots>
						{avatars.map((avatar) => <div key={avatar} className="square-block">
							<div className="doctor-page__image_inner">
								<img src={imageSize(avatar, '_main')} alt="Clinic avatar" />
							</div>
						</div>)}
					</Slider>
				</div>}
				<div className="doctor-page__body">
					<div className="doctor-page__name inner-page__headline">
						<h1>
							{name}
							<div onClick={() => onClick('reviews')} className="pull-right">
								<span className="rate-value">
									{item.averageReviewRate.toLocaleString('en', { maximumFractionDigits: 2 })}
								</span>
								<Rate hideEmpty value={item.averageReviewRate} />
								<span className="review-count">
									{item.totalReviewCount}
									{' '}
									reviews
								</span>
							</div>
							<div className="empty-line" />
						</h1>
					</div>
					<div
						className="doctor-page__description external-html"
						dangerouslySetInnerHTML={{ __html: isEnglish ? item.textEn : item.textEs }}
					/>
				</div>
			</div>
			<div className="clearfix clinic-contact">
				<OfficeInfoBlock
					titleAsH2
					showWorkingHours
					office={item}
				/>
			</div>
			<PageLocations
				items={{ list: item.pages, count: item.countPages } as List<PageLocation>}
				locationId={item.id}
			/>
			<div className="clearfix doctor-card-list" style={{ marginTop: 30 }}>
				{item.doctors?.length > 0 ? <div className="row service-page__block mobile-hidden">
					{item.doctors?.map((item) => <DoctorCard key={item.id} item={item?.doctor} />)}
				</div> : null}
				{item.doctors?.length > 0 ? <div className="slider-arrows service-page__block mobile-show">
					<Slider {...reviewsSliderSettings} dots>
						{item.doctors.map((item) => <DoctorCard key={item.id} item={item?.doctor} />)}
					</Slider>
				</div> : null}
			</div>
			<Reviews id="reviews" reviews={item.reviews} />
			<div className="contacts-page__form">
				<div className="inner-page__headline text-center">
					<h2>{t('site.Schedule an Appointment Now')}</h2>
				</div>
				<ContactUsForm className="general-form" locationId={item.id} />
			</div>
		</div>
	);
};

const ClinicPageComponent: React.FC = () => {
	const { path } = useParams<{ path }>();

	return (
		<ServerPageProvider
			path="clinic"
			inner={(serverPage) =>
				<ItemEditorWrapper
					className=" "
					render={({ actions: { load } }) => <ItemProvider
						id={serverPage?.item?.id || 0}
						type="location"
						readonly
						additionalParams={{ path, published: true, withProfessions: true }}
						loadRequest="getClinicBbyViewRemote"
						item={serverPage?.item}
						skipInitLoad={!!serverPage?.item}
					>
						<ClinicInner load={load} />
					</ItemProvider>}
					backPath="/"
				/>
			}
		/>
	);
};

export default ClinicPageComponent;
